//Bookmarks.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Paper, Typography } from "@mui/material";

import React from "react";

//ESRI javascript API.
import Extent from "@arcgis/core/geometry/Extent";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Viewpoint from "@arcgis/core/Viewpoint";

const toolName: string = "Bookmarks";
const localStorageName: string = "savedBookmarks427";
const customBookmarks_div: string = "Bookmarks_div"; //Container div of the widget.
let userBookmarks: __esri.Bookmarks;
let cannedBookmarks: __esri.Bookmarks;

// Dumbed down version of a bookmark for local storage.
interface iBookmark {
   name: string;
   wkid: number;
   xmin: number;
   ymin: number;
   xmax: number;
   ymax: number;
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   //Util.logMsg("Top of Bookmarks Render");

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      //Util.logMsg("Bookmarks mounted");
      buildBookmarks();

      //Component will unmount.
      return () => {
         saveBookmarks();
         userBookmarks.destroy();
         if (cannedBookmarks) {
            cannedBookmarks.destroy();
         }
      };
   }, []);

   //Create all bookmarks.
   function buildBookmarks() {
      import("@arcgis/core/widgets/Bookmarks").then(({ default: BookmarksWidget }) => {
         import("@arcgis/core/webmap/Bookmark").then(({ default: Bookmark }) => {

            Util.logMsg("Creating Bookmarks widget");

            let bookmk: __esri.Bookmark;
            let bookmks: __esri.Bookmark[] = [];
            let mbr: __esri.Extent;
            let vp: __esri.Viewpoint;

            //Load previously saved bookmarks.
            if (localStorage.Exists(localStorageName)) {
               const cbs = localStorage.GetItem(localStorageName) as iBookmark[];

               for (const cb of cbs) {
                  mbr = new Extent({
                     xmin: cb.xmin, ymin: cb.ymin, xmax: cb.xmax, ymax: cb.ymax,
                     spatialReference: new SpatialReference({ wkid: cb.wkid })
                  });

                  vp = new Viewpoint({ targetGeometry: mbr });

                  bookmk = new Bookmark({ name: cb.name, viewpoint: vp });
                  bookmks.Add(bookmk);
               }
            }

            //Create zoom to full view bookmark so "No Bookmarks" message will not appear.
            if (bookmks.length === 0) {
               vp = new Viewpoint({ targetGeometry: MainMap.mapExtentFull });

               bookmk = new Bookmark({
                  name: "Zoom to full view",
                  viewpoint: vp,
                  //thumbnail: "Images/CountySeal.png",
               });

               bookmks.Add(bookmk);
            }

            //Create bookmarks widget for custom bookmarks.
            userBookmarks = new BookmarksWidget({
               container: customBookmarks_div,
               view: MainMap.activeView,
               editingEnabled: true,
               defaultCreateOptions: {
                  takeScreenshot: true,
                  screenshotSettings: {
                     width: 64,
                     height: 64,
                  },
               },
               bookmarks: bookmks, //Must be initialized to at least [].
            });

            //Process each group heading for canned bookmarks.
            for (let i: number = 0; i < Settings.bookmarks.length; i++) {
               const bookmark: Settings.iBookmark = Settings.bookmarks[i];
               bookmks = [];

               //Process each bookmark.
               for (let j: number = 0; j < bookmark.names.length; j++) {
                  const name: string = bookmark.names[j];
                  const sa: string[] = bookmark.mbrs[j].split(",");

                  mbr = new Extent({
                     xmin: Util.parseFlt(sa[0]), ymin: Util.parseFlt(sa[1]),
                     xmax: Util.parseFlt(sa[2]), ymax: Util.parseFlt(sa[3]),
                     spatialReference: new SpatialReference({ wkid: Settings.wkID })
                  });

                  const viewpoint3 = new Viewpoint;
                  viewpoint3.targetGeometry = mbr;

                  bookmk = new Bookmark({
                     name: name,
                     viewpoint: viewpoint3,
                     //thumbnail: "Images/CountySeal.png",
                  });
                  bookmks.Add(bookmk);
               }

               const tool_div: string = "bookmarkTool_div" + i;

               //Create bookmarks widget for canned bookmarks in the settings.
               cannedBookmarks = new BookmarksWidget({
                  container: tool_div,
                  view: MainMap.activeView,
                  editingEnabled: false,
                  defaultCreateOptions: {
                     takeScreenshot: true,
                     screenshotSettings: {
                        width: 64,
                        height: 64,
                     },
                  },
                  bookmarks: bookmks, //Must be initialized to at least [].
               });
            }

         }); //end import Bookmark.
      }); // end import BookmarksWidget.
   }

   //Save bookmarks.
   function saveBookmarks() {

      //Save custom bookmarks including the first default full extent bookmark if found.
      if (userBookmarks.bookmarks.length > 0) {
         const cbs: iBookmark[] = [];
         let cb: iBookmark;
         let geom: __esri.Geometry;

         for (const bookmrk of userBookmarks.bookmarks) {
            geom = bookmrk.viewpoint.targetGeometry;

            cb = {
               name: bookmrk.name,
               wkid: geom.spatialReference.wkid,
               xmin: geom.extent.xmin,
               ymin: geom.extent.ymin,
               xmax: geom.extent.xmax,
               ymax: geom.extent.ymax,
            };

            cbs.Add(cb);
         }
         localStorage.SetItem(localStorageName, cbs);

      } else {
         localStorage.RemoveItem(localStorageName);
      }
   }

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Create bookmark div.
   function getBookmark(bookmark: Settings.iBookmark, i: number): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      const styles: Default.iStyles = {
         heading: { backgroundColor: Settings.primaryColor, color: 'white', fontWeight: 500, marginTop: 3, padding: 3, paddingLeft: 9 },
         bookmarks: { backgroundColor: Settings.paperColor },
      };

      //Group heading.
      jsx.push(
         <Typography key={Util.uid()} variant="body1" style={styles.heading}>
            {bookmark.heading}
         </Typography>,
      );

      //Process each bookmark div.
      const tool_div: string = "bookmarkTool_div" + i;
      jsx.push(
         <div key={Util.uid()} id={tool_div} style={styles.bookmarks}></div>,
      );

      return jsx;
   }

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   //Styles.
   const styles2: Default.iStyles = {
      bookmarks: { backgroundColor: Settings.paperColor },
   };

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <div id={customBookmarks_div} style={styles2.bookmarks}></div>

            {Settings.bookmarks.map(function (bookmark: Settings.iBookmark, i: number) {
               return (getBookmark(bookmark, i));
            })}
         </div>
      </Paper>
   ); //end return.

} //end function.
